export const REDIRECT_QUERY_PARAM = 'redirect';

export const RETURN_URL_STORAGE_KEY = 'authReturnUrl';

export const AUTH_LANDING_PATH = '/';

export const DISALLOWED_RETURN_PREFIXES = [
  '/signup',
  '/verify',
  '/resetpassword',
  '/forgotpassword',
  '/change-email',
  '/404',
  '/500',
];

const ALWAYS_AUTH_SEGMENTS = ['change-email', 'verify'];

const OAUTH_CALLBACK_PARAMS = ['code', 'state', 'error_description'];

// Params the auth screens read themselves, so they survive the rewrite instead of
// only living inside the `redirect` param.
const AUTH_SCREEN_PARAMS = ['u', 'invitationId', 'invitationCode'];

const isAuthScreenParam = (key: string): boolean =>
  key.startsWith('_g') || AUTH_SCREEN_PARAMS.includes(key);

export interface ReturnUrlLocation {
  pathname: string;
  search: string;
  hash?: string;
}

const getCurrentPath = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const isDisallowedReturnPath = (pathname: string): boolean => {
  const normalized = pathname.toLowerCase().replace(/\/+$/, '') || '/';

  if (ALWAYS_AUTH_SEGMENTS.some((segment) => normalized.includes(segment))) {
    return true;
  }

  return DISALLOWED_RETURN_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
};

const isProtectedDeepLink = (pathname: string): boolean =>
  pathname !== AUTH_LANDING_PATH && !isDisallowedReturnPath(pathname);

export const getSafeRedirect = (raw?: string | null): string | null => {
  if (typeof raw !== 'string') return null;

  const candidate = raw.trim();

  if (!candidate.startsWith('/') || candidate.startsWith('//')) return null;

  if (candidate.includes('\\')) return null;

  let parsed: URL;

  try {
    parsed = new URL(candidate, window.location.origin);
  } catch {
    return null;
  }

  if (parsed.origin !== window.location.origin) return null;
  if (isDisallowedReturnPath(parsed.pathname)) return null;

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

export const readReturnUrl = (): string | null => {
  try {
    return getSafeRedirect(sessionStorage.getItem(RETURN_URL_STORAGE_KEY));
  } catch {
    return null;
  }
};

export const clearReturnUrl = () => {
  try {
    sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
  } catch {
    // storage unavailable (private mode), the query param still carries the path
  }
};

export const persistReturnUrl = (path: string): string | null => {
  const safePath = getSafeRedirect(path);

  if (!safePath) return null;

  try {
    sessionStorage.setItem(RETURN_URL_STORAGE_KEY, safePath);
  } catch {
    // storage unavailable (private mode), the query param still carries the path
  }

  return safePath;
};

export const captureReturnUrl = (
  location: ReturnUrlLocation = window.location,
): string | null => {
  const params = new URLSearchParams(location.search);
  const fromQuery = getSafeRedirect(params.get(REDIRECT_QUERY_PARAM));

  if (fromQuery) {
    return persistReturnUrl(fromQuery);
  }

  params.delete(REDIRECT_QUERY_PARAM);

  const search = params.toString();
  const candidate = getSafeRedirect(
    `${location.pathname}${search ? `?${search}` : ''}${location.hash || ''}`,
  );

  if (!candidate || location.pathname === '/') {
    return readReturnUrl();
  }

  return persistReturnUrl(candidate);
};

export const consumeReturnUrl = (): string | null => {
  const fromQuery = getSafeRedirect(
    new URLSearchParams(window.location.search).get(REDIRECT_QUERY_PARAM),
  );
  const returnUrl = fromQuery || readReturnUrl();

  clearReturnUrl();

  return returnUrl;
};

export const buildRedirectParam = (returnUrl?: string | null): string => {
  const safePath = getSafeRedirect(returnUrl);

  return safePath
    ? `${REDIRECT_QUERY_PARAM}=${encodeURIComponent(safePath)}`
    : '';
};

export const getReturnUrlPathname = (
  returnUrl?: string | null,
): string | null => {
  const safePath = getSafeRedirect(returnUrl);

  return safePath ? new URL(safePath, window.location.origin).pathname : null;
};

export const navigateToPath = (
  path: string,
  { replace = false }: { replace?: boolean } = {},
) => {
  if (getCurrentPath() === path) return;

  if (replace) {
    window.history.replaceState({}, '', path);
  } else {
    window.history.pushState({}, '', path);
  }

  window.dispatchEvent(new PopStateEvent('popstate'));
};

// Swaps a protected deep link for the sign in URL, carrying the destination in
// the `redirect` param. Auth screens keep their own path, and the OAuth callback
// is left alone because Amplify owns that URL.
export const normalizeAuthUrl = (returnUrl?: string | null) => {
  const safePath = getSafeRedirect(returnUrl);

  if (!safePath) return;

  const url = new URL(window.location.href);
  const isOAuthCallback = OAUTH_CALLBACK_PARAMS.some((param) =>
    url.searchParams.has(param),
  );

  if (isOAuthCallback) return;

  if (!isProtectedDeepLink(url.pathname)) {
    url.searchParams.set(REDIRECT_QUERY_PARAM, safePath);
    navigateToPath(`${url.pathname}${url.search}${url.hash}`, {
      replace: true,
    });
    return;
  }

  const params = new URLSearchParams();

  url.searchParams.forEach((value, key) => {
    if (isAuthScreenParam(key)) {
      params.append(key, value);
    }
  });

  params.set(REDIRECT_QUERY_PARAM, safePath);

  navigateToPath(`${AUTH_LANDING_PATH}?${params.toString()}`, {
    replace: true,
  });
};
